import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import * as adminService from '../../services/admin';

const GstReturns = () => {
    const [gstReturns, setGstReturns] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [generateForm, setGenerateForm] = useState({
        branch_id: '',
        return_type: 'GSTR-1',
        period_from: moment().startOf('month').format('YYYY-MM-DD'),
        period_to: moment().endOf('month').format('YYYY-MM-DD')
    });
    const [fileForm, setFileForm] = useState({
        acknowledgement_number: '',
        filing_date: moment().format('YYYY-MM-DD')
    });

    const returnTypes = ['GSTR-1', 'GSTR-3B', 'GSTR-9'];

    useEffect(() => {
        loadBranches();
        loadGstReturns();
    }, []);

    const loadBranches = async () => {
        try {
            const response = await adminService.listBranches();
            if (response.status === 'success') {
                setBranches(response.data);
            }
        } catch (error) {
            console.error('Error loading branches:', error);
        }
    };

    const loadGstReturns = async () => {
        try {
            setLoading(true);
            const response = await adminService.listGstReturns({});
            if (response.status === 'success') {
                setGstReturns(response.data);
            }
        } catch (error) {
            console.error('Error loading GST returns:', error);
            toast.error('Failed to load GST returns');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateReturn = async () => {
        if (!generateForm.branch_id || !generateForm.return_type) {
            toast.error('Please select branch and return type');
            return;
        }

        try {
            setLoading(true);
            const response = await adminService.generateGstReturn(generateForm);
            if (response.status === 'success') {
                toast.success('GST return generated successfully');
                setShowGenerateModal(false);
                loadGstReturns();
            } else {
                toast.error(response.message || 'Failed to generate GST return');
            }
        } catch (error) {
            console.error('Error generating GST return:', error);
            toast.error('Failed to generate GST return');
        } finally {
            setLoading(false);
        }
    };

    const handleFileReturn = async () => {
        if (!fileForm.acknowledgement_number || !fileForm.filing_date) {
            toast.error('Please fill in all fields');
            return;
        }

        try {
            setLoading(true);
            const response = await adminService.fileGstReturn(selectedReturn.id, fileForm);
            if (response.status === 'success') {
                toast.success('GST return filed successfully');
                setShowFileModal(false);
                setSelectedReturn(null);
                loadGstReturns();
            } else {
                toast.error(response.message || 'Failed to file GST return');
            }
        } catch (error) {
            console.error('Error filing GST return:', error);
            toast.error('Failed to file GST return');
        } finally {
            setLoading(false);
        }
    };

    const openFileModal = (gstReturn) => {
        setSelectedReturn(gstReturn);
        setFileForm({
            acknowledgement_number: '',
            filing_date: moment().format('YYYY-MM-DD')
        });
        setShowFileModal(true);
    };

    const getStatusBadge = (status) => {
        const badges = {
            'draft': 'bg-warning',
            'filed': 'bg-success',
            'paid': 'bg-info'
        };
        return badges[status] || 'bg-secondary';
    };

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fa fa-file-alt"></i> GST Returns
                    </h5>
                    <button
                        className="btn btn-light btn-sm"
                        onClick={() => setShowGenerateModal(true)}
                    >
                        <i className="fa fa-plus"></i> Generate Return
                    </button>
                </div>
                <div className="card-body">
                    {gstReturns.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Branch</th>
                                        <th>Return Type</th>
                                        <th>Period</th>
                                        <th>Taxable Sales</th>
                                        <th>CGST</th>
                                        <th>SGST</th>
                                        <th>IGST</th>
                                        <th>Total Tax</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gstReturns.map(ret => (
                                        <tr key={ret.id}>
                                            <td>{ret.branch.branch_name}</td>
                                            <td>
                                                <span className="badge bg-info">{ret.return_type}</span>
                                            </td>
                                            <td>{ret.return_period}</td>
                                            <td className="text-end">
                                                ₹{parseFloat(ret.taxable_sales).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="text-end">
                                                ₹{parseFloat(ret.cgst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="text-end">
                                                ₹{parseFloat(ret.sgst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="text-end">
                                                ₹{parseFloat(ret.igst_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="text-end fw-bold">
                                                ₹{parseFloat(ret.total_tax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(ret.status)} text-capitalize`}>
                                                    {ret.status}
                                                </span>
                                            </td>
                                            <td>
                                                {ret.status === 'draft' && (
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        onClick={() => openFileModal(ret)}
                                                    >
                                                        <i className="fa fa-upload"></i> File
                                                    </button>
                                                )}
                                                {ret.status === 'filed' && (
                                                    <span className="text-muted">
                                                        Ack: {ret.acknowledgement_number}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center text-muted py-5">
                            <i className="fa fa-file-alt fa-3x mb-3"></i>
                            <p>No GST returns generated</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Generate Return Modal */}
            {showGenerateModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Generate GST Return</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowGenerateModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Branch *</label>
                                    <select
                                                className="default-select  form-control wide"
                                        value={generateForm.branch_id}
                                        onChange={(e) => setGenerateForm({ ...generateForm, branch_id: e.target.value })}
                                    >
                                        <option value="">-- Select Branch --</option>
                                        {branches.map(branch => (
                                            <option key={branch.id} value={branch.id}>
                                                {branch.branch_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Return Type *</label>
                                    <select
                                                className="default-select  form-control wide"
                                        value={generateForm.return_type}
                                        onChange={(e) => setGenerateForm({ ...generateForm, return_type: e.target.value })}
                                    >
                                        {returnTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Period From *</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={generateForm.period_from}
                                        onChange={(e) => setGenerateForm({ ...generateForm, period_from: e.target.value })}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Period To *</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={generateForm.period_to}
                                        onChange={(e) => setGenerateForm({ ...generateForm, period_to: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowGenerateModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleGenerateReturn}
                                    disabled={loading}
                                >
                                    <i className="fa fa-cogs"></i> Generate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* File Return Modal */}
            {showFileModal && selectedReturn && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">File GST Return</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => {
                                        setShowFileModal(false);
                                        setSelectedReturn(null);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <strong>Return:</strong> {selectedReturn.return_type} <br />
                                    <strong>Period:</strong> {selectedReturn.return_period} <br />
                                    <strong>Total Tax:</strong> ₹{parseFloat(selectedReturn.total_tax).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Acknowledgement Number *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={fileForm.acknowledgement_number}
                                        onChange={(e) => setFileForm({ ...fileForm, acknowledgement_number: e.target.value })}
                                        placeholder="Enter ARN"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-bold">Filing Date *</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={fileForm.filing_date}
                                        onChange={(e) => setFileForm({ ...fileForm, filing_date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowFileModal(false);
                                        setSelectedReturn(null);
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleFileReturn}
                                    disabled={loading}
                                >
                                    <i className="fa fa-check"></i> File Return
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GstReturns;
